import { useState, useEffect, useCallback } from 'react';
import { relatoriosService } from '../../services';
import { formatCurrency } from '../../utils/format';
import './Dashboard.css';

const hojeISO = () => new Date().toISOString().slice(0, 10);

const formaLabels = {
  dinheiro: { label: 'Dinheiro', icon: '💵' },
  pix: { label: 'Pix', icon: '📱' },
  debito: { label: 'Cartão Débito', icon: '💳' },
  credito: { label: 'Cartão Crédito', icon: '💳' },
  cartao: { label: 'Cartão', icon: '💳' },
  fiado: { label: 'Fiado', icon: '📒' },
};

function labelForma(forma) {
  return formaLabels[forma] || { label: forma ? forma.charAt(0).toUpperCase() + forma.slice(1) : 'Outro', icon: '🧾' };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inicio, setInicio] = useState(hojeISO());
  const [fim, setFim] = useState(hojeISO());

  const carregar = useCallback(() => {
    setLoading(true);
    relatoriosService.dashboard({ inicio, fim })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const definirHoje = () => { const h = hojeISO(); setInicio(h); setFim(h); };
  const intervaloDia = inicio === fim;

  return (
    <div className="dashboard">
      <h1>📊 Dashboard</h1>

      {/* Filtro de período */}
      <div className="card dashboard-filtro">
        <div className="dashboard-filtro-campos">
          <div className="form-group">
            <label>De</label>
            <input type="date" value={inicio} max={fim} onChange={e => setInicio(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Até</label>
            <input type="date" value={fim} min={inicio} onChange={e => setFim(e.target.value)} />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={definirHoje}>Hoje</button>
        </div>
        <span className="dashboard-filtro-info">
          {intervaloDia
            ? `Exibindo dados de ${new Date(inicio + 'T12:00:00').toLocaleDateString('pt-BR')}`
            : `Período: ${new Date(inicio + 'T12:00:00').toLocaleDateString('pt-BR')} a ${new Date(fim + 'T12:00:00').toLocaleDateString('pt-BR')}`}
        </span>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div>Carregando dashboard...</div>
      ) : (
      <>
      {/* KPIs */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-vendas">
          <div className="kpi-icon">💰</div>
          <div className="kpi-info">
            <span className="kpi-label">{intervaloDia ? 'Vendas no dia' : 'Vendas no período'}</span>
            <strong className="kpi-value">{formatCurrency(data?.vendas_hoje)}</strong>
          </div>
        </div>
        <div className="kpi-card kpi-pedidos">
          <div className="kpi-icon">🧾</div>
          <div className="kpi-info">
            <span className="kpi-label">{intervaloDia ? 'Pedidos no dia' : 'Pedidos no período'}</span>
            <strong className="kpi-value">{data?.pedidos_hoje || 0}</strong>
          </div>
        </div>
        <div className="kpi-card kpi-ticket">
          <div className="kpi-icon">🎫</div>
          <div className="kpi-info">
            <span className="kpi-label">Ticket Médio</span>
            <strong className="kpi-value">{formatCurrency(data?.ticket_medio)}</strong>
          </div>
        </div>
        <div className="kpi-card kpi-mesas">
          <div className="kpi-icon">🪑</div>
          <div className="kpi-info">
            <span className="kpi-label">Mesas Ocupadas</span>
            <strong className="kpi-value">{data?.mesas_ocupadas || 0}</strong>
          </div>
        </div>
      </div>

      {/* KPIs por forma de pagamento */}
      <h3 className="dashboard-subtitulo">💳 Vendas por forma de pagamento</h3>
      <div className="kpi-grid">
        {(data?.formas_pagamento || []).map((f, i) => {
          const info = labelForma(f.forma);
          return (
            <div key={i} className="kpi-card kpi-forma">
              <div className="kpi-icon">{info.icon}</div>
              <div className="kpi-info">
                <span className="kpi-label">{info.label} <small>({f.qtd})</small></span>
                <strong className="kpi-value">{formatCurrency(f.total)}</strong>
              </div>
            </div>
          );
        })}
        {(!data?.formas_pagamento || data.formas_pagamento.length === 0) && (
          <p className="no-data">Nenhum pagamento registrado no período</p>
        )}
      </div>

      {/* Gráfico 7 dias */}
      <div className="dashboard-row">
        <div className="card dashboard-chart">
          <h3>📈 Vendas - Últimos 7 dias</h3>
          <div className="chart-bars">
            {(data?.grafico_7dias || []).map((d, i) => {
              const max = Math.max(...(data?.grafico_7dias || []).map(x => parseFloat(x.total) || 0), 1);
              const pct = (parseFloat(d.total) / max) * 100;
              return (
                <div key={i} className="chart-bar-item">
                  <div className="chart-bar-container">
                    <div className="chart-bar" style={{ height: `${pct}%` }}></div>
                  </div>
                  <span className="chart-bar-label">
                    {new Date(d.data).toLocaleDateString('pt-BR', { weekday: 'short' })}
                  </span>
                  <span className="chart-bar-value">{formatCurrency(d.total)}</span>
                </div>
              );
            })}
            {(!data?.grafico_7dias || data.grafico_7dias.length === 0) && (
              <p className="no-data">Sem dados de vendas nos últimos 7 dias</p>
            )}
          </div>
        </div>

        <div className="card dashboard-top">
          <h3>🏆 {intervaloDia ? 'Top Produtos do dia' : 'Top Produtos do período'}</h3>
          <div className="top-list">
            {(data?.top_produtos_hoje || []).map((p, i) => (
              <div key={i} className="top-item">
                <span className="top-pos">{i + 1}º</span>
                <span className="top-emoji">{p.emoji || '🥟'}</span>
                <span className="top-nome">{p.nome}</span>
                <span className="top-qtd">{p.qtd}x</span>
                <span className="top-valor">{formatCurrency(p.total)}</span>
              </div>
            ))}
            {(!data?.top_produtos_hoje || data.top_produtos_hoje.length === 0) && (
              <p className="no-data">Sem vendas registradas no período</p>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
